/**
 * components/profile/LocationMoneyTab.jsx — v3.7.7 Phase 84.31
 *
 * Splits four concerns that used to be conflated in the old Profile /
 * Region sections into distinct, clearly-labelled fields:
 *
 *   • Country of residence   — where you live today.
 *   • Tax residency          — the country whose tax rules apply to you.
 *                              Deliberately NOT inferred from residence
 *                              (expat / cross-border users are the whole
 *                              reason 2120 exists). Optional; blank means
 *                              country-specific tax guidance stays paused.
 *   • City                   — display only, used by the timezone hint.
 *   • Display currency       — the headline currency used in every
 *                              consolidated dashboard total. Change
 *                              triggers a full aggregate refresh.
 *   • Default transaction currency — the currency pre-selected when
 *                              the user manually creates a new
 *                              account / holding / liability. Locked
 *                              once real data exists, to keep historic
 *                              amounts unambiguous.
 *   • Locale + Date format   — display-only preferences.
 *
 * Bug fix from the old surface: currency dropdowns rendered "AED AED"
 * because our CURRENCIES dictionary stores the ISO code as the "symbol"
 * for regional codes. The new `currencyPickerLabel()` helper collapses
 * that duplication.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Lock, Globe, Landmark } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { CURRENCIES, currencyPickerLabel } from '@/lib/format';
import { setBaseCurrency } from '@/lib/currency';
import { useConfirm } from '@/components/ConfirmDialog';
import {
  SectionHeader, SettingsCard, PrimaryButton, TextInput, FieldLabel,
  SelectInput, SavedFlash,
} from '@/components/settings/primitives';

const COUNTRIES = [
  { value: '',                     label: 'Prefer not to say' },
  { value: 'India',                label: 'India' },
  { value: 'United Arab Emirates', label: 'United Arab Emirates' },
  { value: 'Saudi Arabia',         label: 'Saudi Arabia' },
  { value: 'Qatar',                label: 'Qatar' },
  { value: 'Singapore',            label: 'Singapore' },
  { value: 'United Kingdom',       label: 'United Kingdom' },
  { value: 'United States',        label: 'United States' },
  { value: 'Canada',               label: 'Canada' },
  { value: 'Australia',            label: 'Australia' },
  { value: 'New Zealand',          label: 'New Zealand' },
  { value: 'Ireland',              label: 'Ireland' },
  { value: 'Germany',              label: 'Germany' },
  { value: 'Other',                label: 'Somewhere else' },
];

const DATE_FORMATS = [
  { value: 'dd-mmm-yyyy', label: '13 Aug 1985' },
  { value: 'dd/mm/yyyy',  label: '13/08/1985' },
  { value: 'mm/dd/yyyy',  label: '08/13/1985' },
  { value: 'yyyy-mm-dd',  label: '1985-08-13' },
];

const LS_KEY = 'twoone20:regionPrefs';
function loadLocalPrefs() {
  try { return JSON.parse(window.localStorage.getItem(LS_KEY) || '{}') || {}; }
  catch { return {}; }
}
function saveLocalPrefs(next) {
  try { window.localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch { /* private-mode */ }
}

export default function LocationMoneyTab() {
  const { user, profile, session, refreshProfile } = useAuth();
  const confirm = useConfirm();

  // ── Authoritative (Supabase profiles) ──
  const [country, setCountry]     = useState(profile?.country || '');
  const [taxRes,  setTaxRes]      = useState(profile?.tax_residency || '');
  const [city,    setCity]        = useState(profile?.city || '');
  const [baseCur, setBaseCur]     = useState((profile?.base_currency || profile?.currency || 'INR').toUpperCase());
  const [txnCur,  setTxnCur]      = useState((profile?.currency || 'INR').toUpperCase());
  const [saving,  setSaving]      = useState(false);
  const [saved,   setSaved]       = useState(false);
  const [baseSaving, setBaseSaving] = useState(false);

  // ── Display-only prefs (localStorage) ──
  const [dateFormat, setDateFormat] = useState('dd-mmm-yyyy');
  const [prefsSaved, setPrefsSaved] = useState(false);

  // Data-lock probe: currency should not silently change once we have
  // real amounts on the books, or the same INR figure would be quietly
  // re-interpreted as USD.
  const [hasData, setHasData] = useState(false);
  const [hasDataLoaded, setHasDataLoaded] = useState(false);

  useEffect(() => {
    if (!profile) return;
    setCountry(profile.country || '');
    setTaxRes(profile.tax_residency || '');
    setCity(profile.city || '');
    setBaseCur((profile.base_currency || profile.currency || 'INR').toUpperCase());
    setTxnCur((profile.currency || 'INR').toUpperCase());
  }, [profile]);

  // v3.7.12 Phase 84.31 Part 6 — Hydrate tax_residency from the
  // life-profile JSONB bag when the `profiles` row doesn't have it
  // (Prod schemas prior to schema_phase84_31_profile_tax_residency.sql).
  useEffect(() => {
    if (!user?.id) return;
    if (profile?.tax_residency) return; // already have it from the profiles row
    let cancelled = false;
    (async () => {
      try {
        const r = await supabase
          .from('onboarding_profiles')
          .select('life_profile')
          .eq('user_id', user.id)
          .maybeSingle();
        if (cancelled) return;
        const bagTax = r?.data?.life_profile?.tax_residency;
        if (bagTax) setTaxRes(bagTax);
      } catch (_e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [user?.id, profile?.tax_residency]);

  useEffect(() => {
    const p = loadLocalPrefs();
    if (p.dateFormat) setDateFormat(p.dateFormat);
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const [t, a] = await Promise.all([
          supabase.from('transactions').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
          supabase.from('accounts').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
        ]);
        if (cancelled) return;
        setHasData(((t.count || 0) + (a.count || 0)) > 0);
        setHasDataLoaded(true);
      } catch {
        if (!cancelled) setHasDataLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const handleBaseChange = async (next) => {
    const current = (profile?.base_currency || profile?.currency || 'INR').toUpperCase();
    const upper = String(next || '').toUpperCase();
    if (upper === current) return;
    const ok = await confirm({
      title: `Change your display currency to ${upper}?`,
      description: `Your Net Worth, projections, FI tracker and Clarity Score will be recomputed in ${upper}. Individual transactions and account balances stay in their original currencies — only the headline changes.`,
      confirmLabel: `Switch to ${upper}`,
      cancelLabel: 'Keep current',
    });
    if (!ok) { setBaseCur(current); return; }
    setBaseSaving(true);
    const res = await setBaseCurrency(session?.access_token, upper);
    setBaseSaving(false);
    if (res?.ok) {
      toast.success(`Display currency set to ${upper}.`);
      await refreshProfile?.();
    } else {
      toast.error(res?.error || 'Could not change display currency.');
      setBaseCur(current);
    }
  };

  const saveLocation = async () => {
    if (!user) return;
    setSaving(true);
    // v3.7.12 Phase 84.31 Part 6 — Prod schemas may not have the
    // `profiles.tax_residency` column yet. Rather than silently drop
    // the value, we persist it to the life-profile JSONB bag as a
    // durable fallback. The Profile summary endpoint reads from both.
    let taxResSavedElsewhere = false;
    const primaryPayload = {
      country: (country || '').trim() || null,
      city:    (city    || '').trim() || null,
    };
    if (!hasData) primaryPayload.currency = txnCur;
    const primaryWithTax = { ...primaryPayload, tax_residency: (taxRes || '').trim() || null };

    let { error } = await supabase.from('profiles').update(primaryWithTax).eq('id', user.id);

    if (error && /tax_residency/i.test(error.message || '')) {
      // Column missing → save location without it, and mirror the tax
      // choice into the life-profile bag.
      const r2 = await supabase.from('profiles').update(primaryPayload).eq('id', user.id);
      error = r2.error;
      if (!error) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/life-profile`, {
            method:  'PATCH',
            headers: {
              'Content-Type':   'application/json',
              Authorization:    `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({ life_profile: { tax_residency: primaryWithTax.tax_residency } }),
          });
          taxResSavedElsewhere = true;
        } catch (_e) {
          console.warn('life_profile tax_residency fallback failed', _e);
        }
      }
    }

    setSaving(false);
    if (error) {
      toast.error(error.message || 'Could not save.');
      return;
    }
    await refreshProfile?.();
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
    toast.success(taxResSavedElsewhere ? 'Location saved. Tax residency stored in your Life Profile.' : 'Location saved.');
  };

  const savePrefs = () => {
    saveLocalPrefs({ ...loadLocalPrefs(), dateFormat });
    setPrefsSaved(true);
    setTimeout(() => setPrefsSaved(false), 2500);
    toast.success('Display preferences saved.');
  };

  const currencyOptions = useMemo(
    () => CURRENCIES.map((c) => ({ value: c.code, label: currencyPickerLabel(c.code, { withName: true }) })),
    [],
  );

  const residenceTaxMismatch = country && taxRes && country !== taxRes;

  return (
    <div className="space-y-5" data-testid="profile-location-tab">
      <SectionHeader
        title="Location & money"
        subtitle="Where you live, where you pay tax, and how you want money displayed. These may all be different — 2120 supports expat and cross-border scenarios."
      />

      {/* ── Residence & tax ────────────────────────────────────────────── */}
      <SettingsCard className="px-6 py-6">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-5 inline-flex items-center gap-2">
          <Globe className="h-3 w-3" /> Where you are
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <FieldLabel label="Country of residence" hint="Where you live today." />
            <SelectInput
              value={country}
              onChange={setCountry}
              options={COUNTRIES}
              testId="profile-country-residence-select"
            />
          </div>
          <div>
            <FieldLabel
              label="Tax residency"
              hint="The country whose tax rules apply to you. Different from residence for many expats — leave blank until you're sure."
            />
            <SelectInput
              value={taxRes}
              onChange={setTaxRes}
              options={COUNTRIES}
              testId="profile-tax-residency-select"
            />
            <p className="text-[11px] text-muted-foreground/70 mt-1.5 leading-relaxed">
              Country-specific tax and retirement-account guidance stays paused until this is confirmed.
            </p>
          </div>
          <div className="sm:col-span-2">
            <FieldLabel label="City" hint="Optional. Used only to hint at your timezone." />
            <TextInput
              value={city}
              onChange={setCity}
              placeholder="e.g. Dubai, Bengaluru, Singapore"
              testId="profile-city-input"
            />
          </div>
        </div>
        {residenceTaxMismatch && (
          <div
            className="mt-4 rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50/50 dark:bg-amber-950/20 px-3 py-2 text-[12px] text-amber-800 dark:text-amber-300 leading-relaxed"
            role="note"
            data-testid="profile-residence-tax-mismatch"
          >
            You live in <span className="font-medium">{country}</span> but pay tax in{' '}
            <span className="font-medium">{taxRes}</span>. That&rsquo;s completely valid for
            expats &mdash; we&rsquo;ll apply <span className="font-medium">{taxRes}</span> tax rules
            in your plan.
          </div>
        )}
        <div className="mt-5 flex items-center gap-3">
          <PrimaryButton onClick={saveLocation} disabled={saving} testId="profile-location-save">
            {saving ? 'Saving…' : 'Save location'}
          </PrimaryButton>
          <SavedFlash visible={saved} />
        </div>
      </SettingsCard>

      {/* ── Currency: display vs default transaction ──────────────────── */}
      <SettingsCard className="px-6 py-6">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-5 inline-flex items-center gap-2">
          <Landmark className="h-3 w-3" /> Currency
        </p>
        <div className="space-y-4">
          <div>
            <FieldLabel
              label={`Display currency${baseSaving ? ' · Saving…' : ''}`}
              hint="Consolidated dashboard totals, Net Worth, projections and FI tracker are shown in this currency. Individual transactions keep their original currencies."
            />
            <SelectInput
              value={baseCur}
              onChange={(v) => { setBaseCur(v); handleBaseChange(v); }}
              disabled={baseSaving}
              options={currencyOptions}
              testId="profile-display-currency-select"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <label className="text-sm font-medium text-charcoal">
                  Default currency for new entries
                </label>
                {hasData && <Lock className="h-3 w-3 text-muted-foreground" aria-label="Locked" />}
              </div>
              {hasDataLoaded && hasData && (
                <span className="text-[10px] text-muted-foreground">Locked</span>
              )}
            </div>
            <SelectInput
              value={txnCur}
              onChange={setTxnCur}
              disabled={hasData}
              options={currencyOptions}
              testId={hasData ? 'profile-txn-currency-select-locked' : 'profile-txn-currency-select'}
            />
            <p className="text-[11px] text-muted-foreground/70 mt-1.5 leading-relaxed">
              Preselected when you manually add a new transaction, account or holding.
              {hasData
                ? ' Locked because you already have data in this currency — changing it would silently reinterpret historic amounts.'
                : ''}
            </p>
          </div>
        </div>
      </SettingsCard>

      {/* ── Display preferences (locale, date format) ─────────────────── */}
      <SettingsCard className="px-6 py-6">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-5">
          Display
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <FieldLabel label="Date format" />
            <SelectInput
              value={dateFormat}
              onChange={setDateFormat}
              options={DATE_FORMATS}
              testId="profile-date-format-select"
            />
          </div>
        </div>
        <div className="mt-5 flex items-center gap-3">
          <PrimaryButton onClick={savePrefs} testId="profile-prefs-save">
            Save display preferences
          </PrimaryButton>
          <SavedFlash visible={prefsSaved} />
        </div>
      </SettingsCard>
    </div>
  );
}
